import React, { useMemo, useState } from "react";
import styled from "styled-components";
import DataTable from "src/components/Table";
import { Cell } from "src/components/Table/Cell";
import TableCell from "src/components/Table/TableCell";
import TableRow from "src/components/Table/TableRow";
import Search from "src/components/molecules/WorkRequestListViewContainer/components/Search";
import { managerTableHeaders } from "src/components/molecules/TicketsContainer/constant";
import {
  TablePriorityBadge,
  TableStatusDot,
  TableStatusInline,
} from "src/components/molecules/TicketsContainer/styles";
import { getResourseData } from "src/components/molecules/WorkRequestListViewContainer/handler";
import { getAllActiveResources, getPriorityLevels } from "src/redux/selectors/resources";
import { getUserData } from "src/redux/selectors/user";
import { useSelector } from "react-redux";
import { colors } from "src/styles";
import { Ticket } from "src/types";

interface ParentWorkOrderSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrders: Ticket[];
  selectedWonum?: string;
  onConfirm: (wonum: string) => void;
}

export const ParentWorkOrderSelectionModal = ({
  isOpen,
  onClose,
  workOrders,
  selectedWonum,
  onConfirm,
}: ParentWorkOrderSelectionModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState(selectedWonum || "");

  const { employees, vendors } = useSelector(getAllActiveResources);
  const { clientId } = useSelector(getUserData);
  const priorityLevels = useSelector(getPriorityLevels);
  const effectiveSearch = (searchValue || appliedSearch).trim().toLowerCase();

  const displayedWorkOrders = useMemo(() => {
    if (!effectiveSearch) return workOrders;

    return workOrders.filter((workOrder) => {
      const wonum = (workOrder.wonum || "").toLowerCase();
      const description = (workOrder.description || workOrder.name || "").toLowerCase();
      return wonum.includes(effectiveSearch) || description.includes(effectiveSearch);
    });
  }, [workOrders, effectiveSearch]);

  const selectedWorkOrder = useMemo(
    () => workOrders.find((workOrder) => workOrder.wonum === selected),
    [workOrders, selected],
  );

  const closeAndReset = () => {
    setSearchValue("");
    setAppliedSearch("");
    setSelected(selectedWonum || "");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={closeAndReset} />
      <Modal>
        <Header>
          <Title>Select Parent Work Order</Title>
          <CloseButton type="button" onClick={closeAndReset}>
            x
          </CloseButton>
        </Header>

        <Body>
          <SearchWrapper>
            <Search
              value={searchValue}
              applied={!!appliedSearch}
              onChange={setSearchValue}
              onClickApply={() => setAppliedSearch(searchValue.trim())}
              onClickCancel={() => setSearchValue(appliedSearch)}
              onClickClear={() => {
                setSearchValue("");
                setAppliedSearch("");
              }}
            />
          </SearchWrapper>

          <SelectedSummary>
            <SummaryLabel>Selected Parent</SummaryLabel>
            <SummaryValue>
              {selectedWorkOrder
                ? `${selectedWorkOrder.wonum}: ${selectedWorkOrder.description || selectedWorkOrder.name || "Untitled work order"}`
                : "No parent selected"}
            </SummaryValue>
          </SelectedSummary>

          <TableWrapper>
            {displayedWorkOrders.length > 0 ? (
              <DataTable headers={managerTableHeaders}>
                {displayedWorkOrders.map((workOrder) => {
                  const pickedPriorityLevel = priorityLevels?.[workOrder.clientId ?? clientId]?.find(
                    ({ priority }) => priority === workOrder.priorityId,
                  );
                  const assignee =
                    getResourseData(workOrder.vendorId, workOrder.laborId, vendors, employees) ?? "Unassigned";
                  const statusRaw = workOrder.statusDescription ?? workOrder.status ?? "";
                  const statusLabel = statusRaw.replace(/_/g, " ");
                  const isNewStatus = statusRaw.trim().toLowerCase() === "new";
                  const dateParts = (workOrder.dateCreated || "").split(", ");

                  return (
                    <TableRow
                      key={workOrder.wonum}
                      $clickable={true}
                      onClick={() => setSelected(workOrder.wonum)}
                      $bgColor={selected === workOrder.wonum ? colors.grey.light1 : colors.white.default}
                    >
                      <Cell showMark highlight={effectiveSearch}>
                        {workOrder.wonum}
                      </Cell>
                      <Cell highlight={effectiveSearch} bold={dateParts[0] ?? ""}>
                        {(effectiveSearch ? workOrder.dateCreated : `, ${dateParts[1] ?? ""}`) || ""}
                      </Cell>
                      <Cell showMark highlight={effectiveSearch}>
                        {workOrder.client || ""}
                      </Cell>
                      <Cell showMark highlight={effectiveSearch}>
                        {workOrder.location || ""}
                      </Cell>
                      <Cell showMark highlight={effectiveSearch}>
                        {workOrder.description || workOrder.name || ""}
                      </Cell>
                      <TableCell>
                        <TablePriorityBadge
                          color={pickedPriorityLevel ? pickedPriorityLevel.color : colors.grey.default}
                        >
                          {workOrder.priority}
                        </TablePriorityBadge>
                      </TableCell>
                      <TableCell>
                        <TableStatusInline>
                          <TableStatusDot $isNew={isNewStatus} />
                          {statusLabel}
                        </TableStatusInline>
                      </TableCell>
                      <Cell showMark highlight={effectiveSearch}>
                        {assignee}
                      </Cell>
                    </TableRow>
                  );
                })}
              </DataTable>
            ) : (
              <EmptyState>No work orders match your search.</EmptyState>
            )}
          </TableWrapper>
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={closeAndReset}>
            Cancel
          </SecondaryButton>
          <SecondaryButton
            type="button"
            onClick={() => {
              setSelected("");
            }}
          >
            Clear Selection
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!selected}
            onClick={() => {
              if (!selected) return;
              onConfirm(selected);
              closeAndReset();
            }}
          >
            Select Parent
          </PrimaryButton>
        </Footer>
      </Modal>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(1200px, 96vw);
  max-height: 88vh;
  background: #ffffff;
  border: 1px solid #dde2e7;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dde2e7;
  padding: 16px 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2a37;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  color: #6f7782;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px 20px;
  overflow: auto;
`;

const SearchWrapper = styled.div`
  margin-bottom: 12px;

  > div {
    width: 100%;
  }
`;

const SelectedSummary = styled.div`
  border: 1px solid #dde2e7;
  background: #f9fafb;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6f7782;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 13px;
  color: #1f2a37;
`;

const TableWrapper = styled.div`
  border: 1px solid #dde2e7;
`;

const EmptyState = styled.div`
  padding: 28px;
  text-align: center;
  color: #6f7782;
  font-size: 13px;
`;

const Footer = styled.div`
  border-top: 1px solid #dde2e7;
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SecondaryButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  border: 1px solid #1f2a37;
  background: #1f2a37;
  color: #ffffff;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #c9d2dc;
    border-color: #c9d2dc;
    cursor: not-allowed;
  }
`;
